import React, { useState } from 'react'
import { generatePath, Link as RouterLink } from 'react-router-dom'
import moment from 'moment'
import { Button, ReloadIcon } from '@diversioteam/diversio-ds'

import { useCloseSurvey } from 'hooks/pulseSurveys/useClosePulseSurvey'
import { usePulseSurveys } from 'hooks/usePulseSurveys'
import { useGetPulseSurveyDetail } from 'hooks/pulseSurveys/useGetPulseSurveyDetail'
import { generateCompanyURI } from 'sagas/helpers/generateCompanyURI'
import { AppRoute } from 'routing/AppRoute.enum'

import { CloseSurveyProps } from './closeSurvey.types'
import { DETAILS_CONFIG } from './closeSurvey.helpers'
import { CloseSurveyDialog } from './CloseSurveyDialog'
import * as S from './closeSurvey.styles'

const CUSTOM_STYLES_NUMBER_OF_DAYS = 7

export const CloseSurvey = ({ publishDate }: CloseSurveyProps) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false)

    const { activePulseSurvey } = usePulseSurveys()

    const { data } = useGetPulseSurveyDetail({ surveyUUID: activePulseSurvey?.uuid || '' })

    const { mutate, isLoading } = useCloseSurvey()

    if (!activePulseSurvey) {
        return null
    }

    const handleOpenDialog = () => setIsDialogOpen(true)
    const handleCloseDialog = () => setIsDialogOpen(false)

    const handleCloseSurvey = () => {
        mutate({
            surveyUUID: activePulseSurvey.uuid,
        })

        setIsDialogOpen(false)
    }

    if (!publishDate) {
        return null
    }

    if (!data) {
        return null
    }

    const isClosed = data.status === 'ready_for_results'

    const hasCustomStyles = moment().diff(moment(publishDate), 'days') < CUSTOM_STYLES_NUMBER_OF_DAYS

    const getDetailsConfig = () => {
        if (isLoading) {
            return DETAILS_CONFIG.generatingResults
        }

        if (isClosed) {
            return DETAILS_CONFIG.surveyClosed
        }

        return DETAILS_CONFIG.acceptingResponses
    }

    const { title, description } = getDetailsConfig()

    return (
        <S.CloseSurveyWrapper>
            <S.Details>
                <S.Title>{title}</S.Title>

                <S.Description>{description}</S.Description>
            </S.Details>

            {isClosed ? (
                <Button
                    component={RouterLink}
                    to={generateCompanyURI(generatePath(AppRoute.PulseSurveyResults, { id: activePulseSurvey.uuid }))}
                    color="primary"
                >
                    View Results
                </Button>
            ) : (
                <Button
                    color={hasCustomStyles && !isLoading ? 'secondaryLight' : 'primary'}
                    onClick={handleOpenDialog}
                    rightIcon={<ReloadIcon type="line" />}
                    loading={isLoading}
                >
                    Close & Generate Results
                </Button>
            )}

            <CloseSurveyDialog
                open={isDialogOpen}
                onClose={handleCloseDialog}
                onConfirm={handleCloseSurvey}
                numberOfRespondents={data.respondent_count}
            />
        </S.CloseSurveyWrapper>
    )
}
